/**
 * PromoAiProView — halaman promo "Kembali Premium + Google AI Pro" (/promo, /promo/berjaya).
 *
 * Untuk pelanggan Premium yang langganannya sudah tamat LEBIH 30 hari (senarai
 * follow-up 2026-09-15). Bayar RM99 melalui Billplz → sistem rekod sebagai RENEW
 * Premium biasa → admin hantar SATU link pengaktifan Google AI Pro melalui WhatsApp
 * (pelanggan pilih sendiri Gmail yang mahu diaktifkan).
 *
 * Kedudukan dalam App.jsx:
 *   · pelawat BELUM login  → rupa awam: tawaran + "Tahniah" + butang "Login untuk bayar".
 *                            Email & nombor WhatsApp DISOROK (belum tahu akaun siapa).
 *   · SUDAH login          → sama seperti /flow: SELEPAS login, SEBELUM dinding lesen —
 *                            pelanggan sasaran memang lesennya sudah tamat.
 *
 * Semakan kelayakan di sini untuk PAPARAN sahaja (src/utils/promoAiPro.js, kembar
 * server). Server menyemak semula semasa bil dicipta — itulah keputusan sebenar.
 * Rupa awam SENGAJA terus berkata "Tahniah, anda layak" (arahan user 2026-09-15) —
 * link ini hanya dihantar admin kepada senarai yang layak.
 *
 * Nombor WhatsApp diisi sendiri: users.whatsappNumber dahulu, kemudian nombor pada
 * order terkini (diukur 172 calon: 150 akaun + 1 order; 21 tiada langsung = taip sendiri).
 *
 * Suis promo: public_stats/promo_ai_pro { open: true } — FAIL-CLOSED.
 * Pratonton localhost sahaja: /promo?pratonton=awam|layak|ditutup|aktif|tidak|sudah
 *                             /promo/berjaya?pratonton=paid|pending|slow|failed
 */
import { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { httpsCallable } from 'firebase/functions';
import { doc, getDoc, getDocs, collection, query, where, onSnapshot } from 'firebase/firestore';
import { functions, db } from '../firebase';
import { CheckCircle2, Loader2, Clock, XCircle, ShieldCheck, Gift, Check, RefreshCw, PartyPopper, LogIn } from 'lucide-react';
import {
    PROMO_AI_PRO_ID, PROMO_AI_PRO_DOC, PROMO_AI_PRO_RM, PROMO_AI_PRO_BULAN_HADIAH, semakKelayakanPromoAiPro,
} from '../utils/promoAiPro';

import '../design/tokens.css';
import '../design/ui.css';

const WHATSAPP_ADMIN = '601135011231';
const PRATONTON = import.meta.env.DEV;

/* Gaya dalam <style>, BUKAN inline — media query tidak boleh inline, dan pelanggan
   sasaran membuka pautan ini dari WhatsApp di telefon. Awalan `pap-` WAJIB:
   kelas `s-*` milik sistem reka bentuk (pertembungan nama pernah memecahkan tab Flow). */
const PromoStyles = () => (
    <style>{`
@keyframes pap-spin-kf{to{transform:rotate(360deg)}}
.pap-spin{animation:pap-spin-kf 1s linear infinite}

.pap-shell{min-height:100vh;background:var(--s-ground);color:var(--s-ink)}
.pap-top{display:flex;align-items:center;gap:10px;padding:14px 24px;border-bottom:1px solid var(--s-line)}
.pap-logo{width:28px;height:28px;border-radius:8px;flex:none;display:grid;place-items:center;
          background:linear-gradient(135deg,#6366f1,#8b5cf6);color:#fff;font-weight:700;font-size:13px}
.pap-brand{font-size:17px;font-weight:600;letter-spacing:-.01em}
.pap-email{margin-left:auto;font-size:12.5px;color:var(--s-ink-3);max-width:50%;
           overflow:hidden;text-overflow:ellipsis;white-space:nowrap}
.pap-main{display:flex;justify-content:center;padding:36px 20px 56px}
.pap-card{width:100%;max-width:480px;background:var(--s-card);border:1px solid var(--s-line);
          border-radius:var(--s-r-lg);overflow:hidden;box-shadow:0 1px 2px rgba(0,0,0,.05)}
.pap-head{padding:26px 28px 22px}
.pap-body{padding:22px 28px 26px;border-top:1px solid var(--s-line)}
.pap-pad{padding:32px 28px;text-align:center}
/* Jalur "Tahniah" di kepala kad — sama pada rupa awam DAN rupa selepas login. */
.pap-tahniah{display:flex;align-items:center;gap:9px;padding:12px 28px;background:var(--s-ok-soft);
             color:var(--s-ok);font-size:14px;font-weight:600;line-height:1.4;border-bottom:1px solid var(--s-line)}
.pap-tahniah svg{flex:none}
.pap-h1{font-size:24px;font-weight:600;letter-spacing:-.01em;margin:0 0 6px;line-height:1.25}
.pap-price{display:flex;align-items:baseline;gap:8px;margin-top:18px;flex-wrap:wrap}
.pap-price b{font-size:36px;font-weight:600;letter-spacing:-.02em;line-height:1}

.pap-list{display:flex;flex-direction:column;gap:10px;margin:0 0 20px}
.pap-item{display:flex;gap:12px;align-items:flex-start;padding:13px 14px;border:1px solid var(--s-line);
          border-radius:12px;background:var(--s-card)}
.pap-item--hadiah{background:var(--s-fill);border-color:var(--s-line-strong)}
.pap-ico{width:30px;height:30px;border-radius:9px;flex:none;display:grid;place-items:center;
         background:var(--s-fill-hover);color:var(--s-ink)}
.pap-item--hadiah .pap-ico{background:var(--s-primary);color:var(--s-primary-ink)}
.pap-item h3{margin:0 0 3px;font-size:14.5px;font-weight:600;line-height:1.35;
             display:flex;flex-wrap:wrap;align-items:center;gap:3px 8px}
.pap-item p{margin:0;font-size:13px;color:var(--s-ink-2);line-height:1.5}
.pap-tag{display:inline-block;padding:1px 7px;border-radius:6px;font-size:11px;font-weight:700;
         letter-spacing:.03em;background:var(--s-ok-soft);color:var(--s-ok)}

.pap-label{display:block;font-size:12.5px;font-weight:600;color:var(--s-ink-2);margin-bottom:6px}
/* Warna latar/bingkai input datang daripada peraturan global v3.9 (body.s-v39 input — --s-ground).
   font-size 16px SENGAJA: di bawah 16px, Safari iPhone mengezum halaman bila kotak ditekan —
   dan pelanggan sasaran membuka pautan ini dari WhatsApp di telefon. */
.pap-input{width:100%;height:44px;padding:0 12px;border-radius:8px;border:1px solid var(--s-line-strong);
           font-family:inherit;font-size:16px;outline:none;box-sizing:border-box}
/* Akaun = teks biasa, BUKAN kotak — kotak berbingkai dibaca sebagai medan yang boleh ditaip. */
.pap-akaun{margin:0;font-size:15px;font-weight:500;color:var(--s-ink);overflow-wrap:anywhere}
.pap-field{margin-bottom:16px}
.pap-help{margin-top:6px;font-size:12.5px;color:var(--s-ink-3);line-height:1.45}
.pap-err{font-size:13px;color:var(--s-stop);margin:0 0 14px;line-height:1.45}
.pap-btn{width:100%;height:46px;font-size:15px}
.pap-note{display:flex;gap:8px;margin-top:16px;align-items:flex-start}
.pap-note p{margin:0;font-size:12.5px;color:var(--s-ink-3);line-height:1.5}
.pap-terma{margin:14px 0 0;padding:12px 0 0;border-top:1px solid var(--s-line);list-style:none;
           display:flex;flex-direction:column;gap:6px}
.pap-terma li{font-size:12px;color:var(--s-ink-3);line-height:1.5;padding-left:12px;position:relative}
.pap-terma li:before{content:'';position:absolute;left:0;top:8px;width:4px;height:4px;border-radius:50%;background:var(--s-ink-3)}

.pap-statico{margin:0 auto 16px;display:block}
.pap-box{border:1px solid var(--s-line);border-radius:10px;padding:12px 14px;margin-bottom:18px;text-align:left}
.pap-langkah{background:var(--s-fill);border-radius:12px;padding:14px;text-align:left;margin-bottom:16px}
.pap-langkah h3{margin:0 0 4px;font-size:14px;font-weight:600;display:flex;align-items:center;gap:8px}
.pap-langkah p{margin:0;font-size:13px;color:var(--s-ink-2);line-height:1.5}
.pap-gap{height:10px}

@media (max-width:767px){
  .pap-top{padding:11px 14px}
  .pap-tahniah{padding:11px 18px}
  .pap-main{padding:16px 12px 40px}
  .pap-head{padding:22px 18px 18px}
  .pap-body{padding:18px 18px 22px}
  .pap-pad{padding:26px 18px}
  .pap-h1{font-size:21px}
  .pap-price b{font-size:32px}
}
`}</style>
);

function Rangka({ user, children }) {
    return (
        <div className="s-root pap-shell">
            <PromoStyles />
            <header className="pap-top">
                <span className="pap-logo">S</span>
                <span className="pap-brand">SESAAT</span>
                <span className="pap-email">{user?.email || ''}</span>
            </header>
            <main className="pap-main">{children}</main>
        </div>
    );
}

const tarikhMY = (ms) => (ms ? new Date(ms).toLocaleDateString('ms-MY', { day: 'numeric', month: 'long', year: 'numeric' }) : '');

/* Mesej WhatsApp siap-isi — admin terus dapat rujukan order untuk hantar link. */
function waUrl({ orderId, email, amountSen, jenis }) {
    const rm = amountSen ? (amountSen / 100).toFixed(2) : `${PROMO_AI_PRO_RM}.00`;
    const baris = jenis === 'tanya'
        ? [
            'Salam,',
            '',
            'Saya ingin bertanya tentang tawaran Kembali Premium + Google AI Pro.',
            email ? `• Email akaun: ${email}` : null,
        ]
        : [
            'Salam,',
            '',
            'Saya sudah membuat bayaran promo Kembali Premium + Google AI Pro.',
            '',
            `• Jumlah: RM${rm}`,
            email ? `• Email akaun: ${email}` : null,
            orderId ? `• No. rujukan: ${orderId}` : null,
            '',
            'Mohon hantarkan link pengaktifan Google AI Pro. Terima kasih!',
        ];
    return `https://wa.me/${WHATSAPP_ADMIN}?text=${encodeURIComponent(baris.filter((b) => b !== null).join('\n'))}`;
}

/* ─────────── Halaman berjaya (/promo/berjaya) ─────────── */
export function PromoAiProSuccessView({ user }) {
    const [params] = useSearchParams();
    const navigate = useNavigate();
    const orderId = params.get('order');
    const pratonton = PRATONTON ? params.get('pratonton') : null;

    // Keadaan awal diterbitkan terus — jangan setState segerak dalam badan effect.
    const [state, setState] = useState(() => {
        if (pratonton === 'paid') return { phase: 'paid', order: { amountSen: 9900, expiresAt: { toMillis: () => Date.now() + 30 * 864e5 } } };
        if (pratonton === 'pending' || pratonton === 'slow' || pratonton === 'failed') return { phase: pratonton };
        return orderId ? { phase: 'loading' } : { phase: 'notfound' };
    });

    // Sumber kebenaran = doc orders/{orderId} yang ditulis webhook (bukan parameter redirect).
    // Rules benarkan pembeli membaca order sendiri.
    useEffect(() => {
        if (pratonton || !orderId) return undefined;
        const unsub = onSnapshot(
            doc(db, 'orders', orderId),
            (snap) => {
                if (!snap.exists() || snap.data().promo !== PROMO_AI_PRO_ID) { setState({ phase: 'notfound' }); return; }
                const o = snap.data();
                if (o.status === 'paid') setState({ phase: 'paid', order: o });
                else if (o.status === 'failed') setState({ phase: 'failed', order: o });
                else setState((s) => (s.phase === 'slow' ? s : { phase: 'pending', order: o }));
            },
            () => setState({ phase: 'notfound' }),
        );
        // Webhook Billplz lazimnya sampai dalam beberapa saat. Lepas 25s beri penjelasan.
        const t = setTimeout(() => setState((s) => (s.phase === 'pending' || s.phase === 'loading' ? { ...s, phase: 'slow' } : s)), 25000);
        return () => { unsub(); clearTimeout(t); };
    }, [orderId, pratonton]);

    const kad = (children) => (
        <Rangka user={user}>
            <div className="pap-card pap-pad">{children}</div>
        </Rangka>
    );

    if (state.phase === 'loading' || state.phase === 'pending') {
        return kad(
            <>
                <Loader2 size={38} className="pap-spin pap-statico" style={{ color: 'var(--s-ink-3)' }} />
                <h1 className="s-title" style={{ marginBottom: 6 }}>Mengesahkan pembayaran…</h1>
                <p className="s-subtitle">Tunggu sekejap, jangan tutup halaman ini.</p>
            </>,
        );
    }

    if (state.phase === 'paid') {
        const exp = state.order?.expiresAt?.toMillis ? state.order.expiresAt.toMillis() : null;
        return kad(
            <>
                <CheckCircle2 size={44} className="pap-statico" style={{ color: 'var(--s-ok)' }} />
                <h1 className="s-title" style={{ marginBottom: 6 }}>Pembayaran diterima</h1>
                <p className="s-subtitle" style={{ marginBottom: 20 }}>Langganan Premium anda telah disambung.</p>
                {exp && (
                    <div className="pap-box">
                        <p className="s-dim" style={{ marginBottom: 3 }}>Premium aktif sehingga</p>
                        <p style={{ margin: 0, fontSize: 16, fontWeight: 600 }}>{tarikhMY(exp)}</p>
                    </div>
                )}
                <div className="pap-langkah">
                    <h3><Gift size={16} /> Langkah seterusnya: Google AI Pro</h3>
                    <p>
                        Tekan butang di bawah untuk maklumkan admin. Link pengaktifan Google AI Pro akan dihantar
                        ke WhatsApp anda, dan anda boleh pilih sendiri Gmail yang mahu digunakan.
                    </p>
                </div>
                <a className="s-btn s-btn--primary pap-btn" style={{ textDecoration: 'none' }}
                    href={waUrl({ orderId, email: user?.email, amountSen: state.order?.paidAmountSen || state.order?.amountSen })}
                    target="_blank" rel="noreferrer">
                    Maklumkan admin di WhatsApp
                </a>
                <p className="s-dim" style={{ marginTop: 10 }}>Mesej sudah siap ditaip — anda cuma perlu tekan hantar.</p>
                <div className="pap-gap" />
                <button type="button" className="s-btn s-btn--ghost pap-btn" onClick={() => navigate('/workspace')}>
                    Masuk Workspace
                </button>
            </>,
        );
    }

    if (state.phase === 'slow') {
        return kad(
            <>
                <Clock size={40} className="pap-statico" style={{ color: 'var(--s-warn)' }} />
                <h1 className="s-title" style={{ marginBottom: 6 }}>Pembayaran sedang disahkan</h1>
                <p className="s-subtitle" style={{ marginBottom: 20 }}>
                    Pengesahan mengambil masa lebih lama daripada biasa. Kalau duit sudah ditolak, langganan anda
                    tetap akan disambung. Simpan nombor rujukan ini.
                </p>
                {orderId && <p className="s-num" style={{ marginBottom: 20, wordBreak: 'break-all' }}>{orderId}</p>}
                <a className="s-btn s-btn--primary pap-btn" style={{ textDecoration: 'none' }}
                    href={waUrl({ orderId, email: user?.email })} target="_blank" rel="noreferrer">
                    Maklumkan admin di WhatsApp
                </a>
                <div className="pap-gap" />
                <button type="button" className="s-btn s-btn--ghost pap-btn" onClick={() => window.location.reload()}>
                    <RefreshCw size={14} /> Semak semula
                </button>
            </>,
        );
    }

    if (state.phase === 'failed') {
        return kad(
            <>
                <XCircle size={40} className="pap-statico" style={{ color: 'var(--s-stop)' }} />
                <h1 className="s-title" style={{ marginBottom: 6 }}>Pembayaran tidak selesai</h1>
                <p className="s-subtitle" style={{ marginBottom: 20 }}>Tiada apa-apa ditolak. Anda boleh cuba semula.</p>
                <button type="button" className="s-btn s-btn--primary pap-btn" onClick={() => navigate('/promo')}>Cuba semula</button>
            </>,
        );
    }

    return kad(
        <>
            <XCircle size={40} className="pap-statico" style={{ color: 'var(--s-ink-3)' }} />
            <h1 className="s-title" style={{ marginBottom: 6 }}>Pesanan tidak dijumpai</h1>
            <p className="s-subtitle" style={{ marginBottom: 20 }}>
                Kami tidak menemui pesanan ini pada akaun anda. Jika anda sudah membuat pembayaran, sila hubungi admin.
            </p>
            <a className="s-btn s-btn--primary pap-btn" style={{ textDecoration: 'none' }}
                href={waUrl({ orderId, email: user?.email })} target="_blank" rel="noreferrer">
                Hubungi admin di WhatsApp
            </a>
        </>,
    );
}

/* ─────────── Halaman promo (/promo) ─────────── */
const MESEJ_TIDAK_LAYAK = {
    masih_aktif: {
        tajuk: 'Langganan anda masih aktif',
        ayat: 'Langganan Premium anda masih aktif, jadi tawaran ini tidak diperlukan untuk akaun anda.',
    },
    sudah_tuntut: {
        tajuk: 'Tawaran sudah diterima',
        ayat: 'Akaun ini sudah menerima tawaran Google AI Pro. Tawaran ini hanya sekali bagi setiap akaun.',
    },
    // ⚠️ SENGAJA tidak menyebut "30 hari" — pelanggan yang baru tamat tidak patut belajar
    //    bahawa menangguhkan renew memberinya hadiah. Sama dengan mesej server.
    lain: {
        tajuk: 'Tawaran tidak tersedia',
        ayat: 'Tawaran ini khas untuk pelanggan Premium lama tertentu, dan tidak tersedia untuk akaun ini.',
    },
};

const sahNombor = (v) => String(v || '').replace(/\D/g, '').length >= 9;
const masaMs = (ts) => (!ts ? 0 : typeof ts.toMillis === 'function' ? ts.toMillis()
    : (ts.seconds ? ts.seconds * 1000 : new Date(ts).getTime() || 0));

/* Nombor WhatsApp yang diisi sendiri: akaun dahulu, kemudian order terkini yang membawa
   nombor sah (turutan sama dengan senarai follow-up). Tiada → '' = pelanggan taip sendiri. */
function nomborWhatsAppAkaun(dataAkaun, pesanan) {
    if (sahNombor(dataAkaun?.whatsappNumber)) return String(dataAkaun.whatsappNumber).trim();
    const terkini = (pesanan || [])
        .filter((o) => sahNombor(o.whatsappNumber))
        .sort((a, b) => masaMs(b.createdAt) - masaMs(a.createdAt))[0];
    return terkini ? String(terkini.whatsappNumber).trim() : '';
}

/* Pratonton localhost — keadaan awal setiap mod. Mod tidak dikenali = 'layak'. */
const PRATONTON_FASA = {
    awam: () => ({ phase: 'awam' }),
    layak: () => ({ phase: 'layak', tamatMs: Date.now() - 45 * 864e5 }),
    ditutup: () => ({ phase: 'ditutup', mesej: '' }),
    aktif: () => ({ phase: 'tidak', sebab: 'masih_aktif' }),
    sudah: () => ({ phase: 'tidak', sebab: 'sudah_tuntut' }),
    tidak: () => ({ phase: 'tidak', sebab: 'belum_cukup_tempoh' }),
};

/* Kepala tawaran + senarai isi + nota bayaran — DIKONGSI rupa awam dan rupa selepas
   login. Satu takrifan supaya dua rupa tidak terpesong (harga, tempoh, terma). */
function KepalaTawaran() {
    return (
        <>
            <div className="pap-tahniah">
                <PartyPopper size={17} />
                <span>Tahniah! Anda layak untuk tawaran ini.</span>
            </div>
            <div className="pap-head">
                <h1 className="pap-h1">Kembali ke SESAAT Premium</h1>
                <p className="s-subtitle">Sambung langganan Premium anda dan terima Google AI Pro secara percuma.</p>
                <div className="pap-price">
                    <b>RM{PROMO_AI_PRO_RM}</b>
                    <span className="s-subtitle">untuk 30 hari Premium</span>
                </div>
            </div>
        </>
    );
}

function SenaraiIsi() {
    return (
        <div className="pap-list">
            <div className="pap-item">
                <span className="pap-ico"><Check size={16} /></span>
                <div>
                    <h3>SESAAT Premium — 30 hari</h3>
                    <p>Langganan anda disambung serta-merta selepas bayaran berjaya.</p>
                </div>
            </div>
            <div className="pap-item pap-item--hadiah">
                <span className="pap-ico"><Gift size={16} /></span>
                <div>
                    <h3><span>Google AI Pro — {PROMO_AI_PRO_BULAN_HADIAH} bulan</span><span className="pap-tag">PERCUMA</span></h3>
                    <p>
                        Admin akan hantar satu link pengaktifan melalui WhatsApp. Anda pilih sendiri
                        Gmail yang mahu digunakan.
                    </p>
                </div>
            </div>
        </div>
    );
}

function NotaBayaran() {
    return (
        <>
            <div className="pap-note">
                <ShieldCheck size={15} style={{ color: 'var(--s-ink-3)', flex: 'none', marginTop: 2 }} />
                <p>Bayaran diproses oleh Billplz (FPX / kad). Ini bayaran sekali — tiada caj automatik dikenakan.</p>
            </div>
            <ul className="pap-terma">
                <li>Tawaran ini hanya sekali bagi setiap akaun.</li>
                <li>
                    Google AI Pro disediakan oleh Google. Tempoh {PROMO_AI_PRO_BULAN_HADIAH} bulan tertakluk kepada
                    Google tidak membatalkan pelan tersebut.
                </li>
            </ul>
        </>
    );
}

export default function PromoAiProView({ user }) {
    const [params] = useSearchParams();
    const navigate = useNavigate();
    const pratonton = PRATONTON ? params.get('pratonton') : null;
    const fasaPratonton = pratonton ? (PRATONTON_FASA[pratonton] ? pratonton : 'layak') : null;

    const [muat, setMuat] = useState(0);        // naikkan untuk cuba baca semula
    // Pratonton (localhost sahaja) diterbitkan terus di sini — jangan setState segerak dalam effect.
    const [state, setState] = useState(() => (fasaPratonton ? PRATONTON_FASA[fasaPratonton]() : { phase: 'loading' }));
    // Pratonton 'layak' memapar nombor yang sudah diisi sendiri — rupa yang kebanyakan pelanggan nampak.
    const [phone, setPhone] = useState(() => (fasaPratonton === 'layak' ? '+60123456789' : ''));
    const [phoneAuto, setPhoneAuto] = useState(() => fasaPratonton === 'layak');
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState('');

    useEffect(() => {
        if (pratonton) return undefined;
        const uid = user?.uid || null;
        let batal = false;
        (async () => {
            try {
                // Suis promo boleh dibaca tanpa akaun (public_stats) — rupa awam turut ikut buka/tutup.
                const [suisSnap, licSnap, ordSnap, userSnap] = await Promise.all([
                    getDoc(doc(db, 'public_stats', PROMO_AI_PRO_DOC)),
                    uid ? getDoc(doc(db, 'licenses', uid)) : null,
                    uid ? getDocs(query(collection(db, 'orders'), where('uid', '==', uid))) : null,
                    uid ? getDoc(doc(db, 'users', uid)) : null,
                ]);
                if (batal) return;

                const suis = suisSnap.exists() ? suisSnap.data() : {};
                if (suis.open !== true) {
                    setState({ phase: 'ditutup', mesej: typeof suis.closedMessage === 'string' ? suis.closedMessage.trim() : '' });
                    return;
                }
                if (!uid) { setState({ phase: 'awam' }); return; }

                const pesanan = ordSnap.docs.map((d) => d.data());
                // Borang belum dipaparkan semasa bacaan ini (fasa 'loading'), jadi tiada apa yang
                // pelanggan sempat taip untuk ditimpa.
                const nombor = nomborWhatsAppAkaun(userSnap.exists() ? userSnap.data() : {}, pesanan);
                if (nombor) { setPhone(nombor); setPhoneAuto(true); }

                const r = semakKelayakanPromoAiPro({
                    lesen: licSnap.exists() ? licSnap.data() : null,
                    pesanan,
                });
                setState(r.layak ? { phase: 'layak', tamatMs: r.tamatMs } : { phase: 'tidak', sebab: r.sebab });
            } catch (e) {
                console.warn('[PromoAiPro] Gagal memuat tawaran:', e);
                if (!batal) setState({ phase: 'ralat' });
            }
        })();
        return () => { batal = true; };
    }, [user?.uid, pratonton, muat]);

    const loginUntukBayar = () => {
        // Pratonton: tunjuk terus rupa selepas login (tiada login sebenar di localhost).
        if (pratonton) { window.location.assign('/promo?pratonton=layak'); return; }
        // Ingat destinasi — App.jsx memulangkan pelanggan ke /promo sebaik login selesai.
        // Kunci SAMA dengan lencongan /flow (App.jsx `sesaat_flow_next`).
        try { sessionStorage.setItem('sesaat_flow_next', `/promo${window.location.search}`); } catch { /* storage disabled — abaikan */ }
        navigate('/login');
    };

    const submit = async (e) => {
        e.preventDefault();
        setErr('');
        if (pratonton) { setErr('Pratonton sahaja — bayaran tidak dimulakan.'); return; }
        setBusy(true);
        try {
            // Emel TIDAK dihantar — server ambil daripada akaun yang log masuk.
            const fn = httpsCallable(functions, 'createPromoAiProBill');
            const { data } = await fn({ whatsappNumber: phone, returnOrigin: window.location.origin });
            window.location.href = data.url;   // ke Billplz
        } catch (e2) {
            setErr(e2?.message || 'Gagal memulakan pembayaran. Sila cuba lagi.');
            setBusy(false);
        }
    };

    if (state.phase === 'loading') {
        return (
            <Rangka user={user}>
                <div className="pap-card pap-pad">
                    <Loader2 size={32} className="pap-spin pap-statico" style={{ color: 'var(--s-ink-3)' }} />
                    <p className="s-subtitle">Menyediakan tawaran anda…</p>
                </div>
            </Rangka>
        );
    }

    if (state.phase === 'ralat') {
        return (
            <Rangka user={user}>
                <div className="pap-card pap-pad">
                    <XCircle size={36} className="pap-statico" style={{ color: 'var(--s-ink-3)' }} />
                    <h1 className="s-title" style={{ marginBottom: 6 }}>Tawaran tidak dapat dipaparkan</h1>
                    <p className="s-subtitle" style={{ marginBottom: 20 }}>Sambungan terganggu. Sila cuba sekali lagi.</p>
                    <button type="button" className="s-btn s-btn--primary pap-btn"
                        onClick={() => { setState({ phase: 'loading' }); setMuat((n) => n + 1); }}>
                        <RefreshCw size={14} /> Cuba semula
                    </button>
                </div>
            </Rangka>
        );
    }

    if (state.phase === 'ditutup' || state.phase === 'tidak') {
        const m = state.phase === 'ditutup'
            ? { tajuk: 'Tawaran sudah ditutup', ayat: state.mesej || 'Tawaran ini sudah ditutup. Anda masih boleh menyambung langganan seperti biasa.' }
            : (MESEJ_TIDAK_LAYAK[state.sebab] || MESEJ_TIDAK_LAYAK.lain);
        const aktif = state.phase === 'tidak' && (state.sebab === 'masih_aktif' || state.sebab === 'sudah_tuntut');
        return (
            <Rangka user={user}>
                <div className="pap-card pap-pad">
                    <Gift size={36} className="pap-statico" style={{ color: 'var(--s-ink-3)' }} />
                    <h1 className="s-title" style={{ marginBottom: 6 }}>{m.tajuk}</h1>
                    <p className="s-subtitle" style={{ marginBottom: 20 }}>{m.ayat}</p>
                    {aktif ? (
                        <button type="button" className="s-btn s-btn--primary pap-btn" onClick={() => navigate('/workspace')}>
                            Masuk Workspace
                        </button>
                    ) : (
                        <button type="button" className="s-btn s-btn--primary pap-btn" onClick={() => navigate('/pricing')}>
                            Lihat pakej langganan
                        </button>
                    )}
                    <div className="pap-gap" />
                    <a className="s-btn s-btn--ghost pap-btn" style={{ textDecoration: 'none' }}
                        href={waUrl({ email: user?.email, jenis: 'tanya' })} target="_blank" rel="noreferrer">
                        Hubungi admin di WhatsApp
                    </a>
                </div>
            </Rangka>
        );
    }

    // phase === 'awam' — pelawat belum login. Email + WhatsApp SENGAJA tiada: kita belum
    // tahu akaun siapa. Butang membawa ke login; selepas login, borang sudah terisi.
    if (state.phase === 'awam') {
        return (
            <Rangka user={null}>
                <div className="pap-card">
                    <KepalaTawaran />
                    <div className="pap-body">
                        <SenaraiIsi />
                        <button type="button" className="s-btn s-btn--primary pap-btn" onClick={loginUntukBayar}>
                            <LogIn size={16} /> Login untuk bayar RM{PROMO_AI_PRO_RM}
                        </button>
                        <p className="pap-help" style={{ textAlign: 'center', marginTop: 10 }}>
                            Login dengan akaun SESAAT yang anda gunakan sebelum ini. Maklumat akaun anda akan diisi secara automatik.
                        </p>
                        <NotaBayaran />
                    </div>
                </div>
            </Rangka>
        );
    }

    // phase === 'layak'
    return (
        <Rangka user={user}>
            <div className="pap-card">
                <KepalaTawaran />

                <form onSubmit={submit} className="pap-body">
                    <SenaraiIsi />

                    <div className="pap-field">
                        <span className="pap-label">Akaun</span>
                        <p className="pap-akaun">{user?.email || '—'}</p>
                        {state.tamatMs && (
                            <p className="pap-help">Langganan Premium akaun ini tamat pada {tarikhMY(state.tamatMs)}.</p>
                        )}
                    </div>
                    <div className="pap-field">
                        <label className="pap-label" htmlFor="pap-phone">Nombor WhatsApp</label>
                        <input id="pap-phone" className="pap-input" value={phone} required inputMode="tel"
                            autoComplete="tel" placeholder="011-2345 6789"
                            onChange={(ev) => { setPhone(ev.target.value); setPhoneAuto(false); }} />
                        <p className="pap-help">
                            {phoneAuto
                                ? 'Nombor ini diambil daripada akaun anda — tukar jika mahu guna nombor lain. Link pengaktifan Google AI Pro dihantar ke nombor ini.'
                                : 'Link pengaktifan Google AI Pro dihantar ke nombor ini.'}
                        </p>
                    </div>

                    {err && <p className="pap-err">{err}</p>}

                    <button type="submit" className="s-btn s-btn--primary pap-btn" disabled={busy}>
                        {busy
                            ? <><Loader2 size={15} className="pap-spin" /> Menyediakan pembayaran…</>
                            : `Bayar RM${PROMO_AI_PRO_RM} melalui Billplz`}
                    </button>

                    <NotaBayaran />
                </form>
            </div>
        </Rangka>
    );
}
